export const contentPermission = {
  "roles-workspace": {
    title: "Workspace Roles and Permissions ",
    content: `
        <p>Anydone provides role-based access control, allowing you to customize permissions on a granular level for each workspace member. You can add members to specific areas of your workspace, change their roles to admin and owner, or update their permissions, giving you full control over who can view, manage, and change them.</p>
        <p>There are three types of workspace roles, and you can customize permissions within each role as needed.</p>

        <ol>
        <li><strong>Workspace Owner:</strong>  Owner holds the highest level of permissions, with full access to manage and control the workspace and its settings.</li>
        <li><strong>Workspace Admin:</strong>  Admin has full access to manage and control, however, the Workspace Owner can limit or remove the admin's permissions, change their role, or restrict their control over the workspace as needed.</li>
        <li><strong>Workspace Member: </strong> Members have no access to manage by default, however, the Workspace Admin or Owner can customize their permissions within the member’s role or change their role and allow them manage workspace as required.</li>
        </ol>

        <h3 class="headingThree underline-text">Managing Workspace</h3>
        <ol class="custom-list">
        <li><strong>Managing Members</strong>(Invite, Change Role, Deactivate, Reinvite )<br>
        Owners and admins have the permission to manage members. However, the owner can remove or change admin permission or add workspace members to manage members.
        </li>
        <li><strong>Managing CoSpace</strong>(Create, Delete, Archive/Unarchive)<br>
        Owners and admins have the permission to manage the CoSpace, but the owner can remove or change admin permissions or add workspace members to manage the CoSpace.
        </li>
        <li><strong>Managing Projects </strong>(Create, Archive/Unarchive, Manage Folders, Add Members)<br>
        Owners and admins have the permission to manage the projects, although the owner can remove admin permissions or add members from workspace to manage projects.
        </li>
        <li><strong>Managing automation tools </strong>(Create, Edit, Delete, Integrate)<br>
        Owners and admins have the permission to control and manage, whereas members are limited to viewing automation tools. However, the owner can remove or update admin permissions or add workspace members, to create, edit, delete, and integrate automation tools.
        </li>
        <li><strong>Managing apps </strong>(Create, Edit, Delete, Integrate)<br>
        Owners and admins have the permission to control and manage, likewise members are limited to viewing all the created  app directories. However, the owner can remove or update admin permissions or add workspace members, to create, edit, delete, and integrate apps.
        </li>
        </ol>

        <h3 class="headingThree underline-text">Things to remember</h3>
        <ul>
        <li>Only the owner can delete the workspace or edit the workspace details.</li>
        <li>Making changes in workspace <strong>General settings</strong> is limited to the Workspace Owners only.</li>
        <li>External teams cannot view app directory and automation tools of workspace.</li>
        </ul>
        `,
  },
  "roles-projects": {
    title: "Project Roles and Permissions",
    content: `
      <p>In anydone, the Project Owner has the highest level of permissions to manage projects and the Workspace Owners and Admins are assigned as Project Owners in every project, no matter who created it. Project roles are fixed, and their permissions cannot be customized.</p>
      <p>Project Owners can add or remove members from the project, assign them to specific project folders with the appropriate roles, or change their roles within the project and project folders. Only project owners can create project folders, archive/unarchive projects, and its folders.</p>
      <p>For instance, added members can view and make changes only to the project folders they are added to, with respect to their assigned role.</p>
    <p>Project roles are four types: Project owner, folder admin, editor, and viewer. </p>

    <ol type="a">
    <li><strong>Project Owner:</strong> The Project Owner has full control to manage the project, with unrestricted access, the Project Owner can perform any action within the project and its folders.</li>
    <li><strong>Admin:</strong> Admin has permissions to manage components within a project folder but cannot create new folders. They can create, edit, delete, and archive the folder’s components within the assigned project folder.</li>
    <li><strong>Editor:</strong> Editor can create and edit components within the assigned project folders. However, they cannot remove, delete or archive folder components that were not created by them.</li>
    <li><strong>Viewer:</strong> Viewer has view-only access to the assigned folder, allowing them to view its components only. They do not have permission to make any changes of any kind to the folders they are assigned to.</li>
    </ol>

    <h3 class="underline-text">Things to remember</h3>
    <ul>
    <li>Changing project status is limited to project owners</li>
    <li>Viewing project dashboard is limited to project owners</li>
    <li>Editing / archiving / unarchiving project and project folders is limited to owners</li>
    </ul>

    <h3 class="headingThree">Ticket Folder</h3>
    <div class="permission-table">
    <table>
        <thead>
            <th>Permission</th>
            <th>Admin</th>
            <th>Editor</th>
            <th>Viewer</th>
        </thead>
        <tbody>
        <tr>
            <td>Create ticket</td>
            <td>Yes</td>
            <td>Yes</td>
            <td>No</td>
        </tr>
        <tr>
            <td>Edit / archive ticket folder</td>
            <td>No</td>
            <td>No</td>
            <td>No</td>
        </tr>
        <tr>
            <td>Delete / archive ticket</td>
            <td>Yes</td>
            <td>Yes (only created by themselves)</td>
            <td>No</td>
        </tr>
        <tr>
            <td>Move ticket</td>
            <td>Yes</td>
            <td>No</td>
            <td>No</td>
        </tr>
        <tr>
            <td>Clone ticket</td>
            <td>Yes</td>
            <td>Yes</td>
            <td>No</td>
        </tr>
        <tr>
            <td>Share ticket</td>
            <td>Yes</td>
            <td>Yes</td>
            <td>Yes</td>
        </tr>
        <tr>
            <td>Edit ticket details</td>
            <td>Yes</td>
            <td>Yes</td>
            <td>No</td>
        </tr>
        <tr>
            <td>Change ticket status / priority</td>
            <td>Yes</td>
            <td>Yes</td>
            <td>No</td>
        </tr>
        <tr>
            <td>Add or generate ticket sub tasks & checklists</td>
            <td>Yes</td>
            <td>Yes</td>
            <td>No</td>
        </tr>
        <tr>
            <td>Add / remove members from folder</td>
            <td>No</td>
            <td>No</td>
            <td>No</td>
        </tr>
        <tr>
            <td>Create custom fields</td>
            <td>Yes</td>
            <td>No</td>
            <td>No</td>
        </tr>
        <tr>
            <td>Create project teams</td>
            <td>Yes</td>
            <td>No</td>
            <td>No</td>
        </tr>
        <tr>
            <td>Project settings</td>
            <td>Yes</td>
            <td>No</td>
            <td>No</td>
        </tr>
        <tr>
            <td>Ticket workflow</td>
            <td>Yes</td>
            <td>Yes</td>
            <td>No</td>
        </tr>
        <tr>
            <td>Import / export tickets</td>
            <td>Yes</td>
            <td>Yes</td>
            <td>No</td>
        </tr>
        <tr>
            <td>Add labels</td>
            <td>Yes</td>
            <td>Yes</td>
            <td>No</td>
        </tr>
        </tbody>
    </table>
    </div>

    <h3 class="headingThree">Sprint Folder</h3>
    <div class="permission-table">
        <table>
        <thead>
            <th>Permission</th>
            <th>Admin</th>
            <th>Editor</th>
            <th>Viewer</th>
        </thead>
        <tbody>
        <tr>
            <td>Create New Sprint</td>
            <td>Yes</td>
            <td>Yes</td>
            <td>No</td>
        </tr>
        <tr>
            <td>Change Sprint Status</td>
            <td>Yes</td>
            <td>Yes</td>
            <td>No</td>
        </tr>
        <tr>
            <td>Workflow</td>
            <td>Yes</td>
            <td>No</td>
            <td>No</td>
        </tr>
        <tr>
            <td>Settings</td>
            <td>Yes</td>
            <td>No</td>
            <td>No</td>
        </tr>
        <tr>
        <td colspan="4">All the sprint ticket permissions follow the same structure as ticket folder permissions.</td>
        </tr>
        </tbody>
        </table>
    </div>

    <h3 class="headingThree">Form Folder</h3>
    <div class="permission-table">
        <table>
        <thead>
            <th>Permission</th>
            <th>Admin</th>
            <th>Editor</th>
            <th>Viewer</th>
        </thead>
        <tbody>
        <tr>
            <td>Create forms</td>
            <td>Yes</td>
            <td>Yes</td>
            <td>No</td>
        </tr>
        <tr>
            <td>Share forms</td>
            <td>Yes</td>
            <td>Yes</td>
            <td>Yes</td>
        </tr>
        <tr>
            <td>View Response</td>
            <td>Yes</td>
            <td>Yes</td>
            <td>Yes</td>
        </tr>
        <tr>
            <td>Edit form</td>
            <td>Yes</td>
            <td>Yes</td>
            <td>No</td>
        </tr>
        <tr>
            <td>Customize forms</td>
            <td>Yes</td>
            <td>Yes</td>
            <td>No</td>
        </tr>
        <tr>
            <td>Delete forms</td>
            <td>Yes</td>
            <td>Yes (only created by themselves)</td>
            <td>No</td>
        </tr>
        </tbody>
        </table>
    </div>

    <h3 class="headingThree">Paper Folder</h3>
    <div class="permission-table">
        <table>
        <thead>
            <th>Permission</th>
            <th>Admin</th>
            <th>Editor</th>
            <th>Viewer</th>
        </thead>
        <tbody>
        <tr>
            <td>Create paper</td>
            <td>Yes</td>
            <td>Yes</td>
            <td>No</td>
        </tr>
        <tr>
            <td>Edit paper</td>
            <td>Yes</td>
            <td>Yes</td>
            <td>No</td>
        </tr>
        <tr>
            <td>Share paper</td>
            <td>Yes</td>
            <td>Yes</td>
            <td>Yes</td>
        </tr>
        <tr>
            <td>Delete paper</td>
            <td>Yes</td>
            <td>Yes (only created by themselves)</td>
            <td>No</td>
        </tr>
        <tr>
            <td>Duplicate</td>
            <td>Yes</td>
            <td>Yes</td>
            <td>No</td>
        </tr>
        </tbody>
        </table>
    </div>

    <h3 class="headingThree">Report Folder</h3>
    <div class="permission-table">
        <table>
        <thead>
            <th>Permission</th>
            <th>Admin</th>
            <th>Editor</th>
            <th>Viewer</th>
        </thead>
        <tbody>
        <tr>
            <td>Create report</td>
            <td>Yes</td>
            <td>Yes</td>
            <td>No</td>
        </tr>
        <tr>
            <td>Share report</td>
            <td>Yes</td>
            <td>Yes</td>
            <td>Yes</td>
        </tr>
        <tr>
            <td>Edit report</td>
            <td>Yes</td>
            <td>Yes</td>
            <td>No</td>
        </tr>
        <tr>
            <td>Delete report</td>
            <td>Yes</td>
            <td>Yes(only created by themselves)</td>
            <td>No</td>
        </tr>
        </tbody>
        </table>
    </div>

    <h3 class="headingThree">Roadmap Folder</h3>
    <div class="permission-table">
        <table>
        <thead>
            <th>Permission</th>
            <th>Admin</th>
            <th>Editor</th>
            <th>Viewer</th>
        </thead>
        <tbody>
        <tr>
            <td>Create roadmap</td>
            <td>Yes</td>
            <td>Yes</td>
            <td>No</td>
        </tr>
        <tr>
            <td>Edit roadmap(change priority, dates, status, description, title)</td>
            <td>Yes</td>
            <td>Yes</td>
            <td>No</td>
        </tr>
        <tr>
            <td>Delete roadmap</td>
            <td>Yes</td>
            <td>Yes(only created by themselves)</td>
            <td>No</td>
        </tr>
        <tr>
            <td>Add blocked by</td>
            <td>Yes</td>
            <td>Yes</td>
            <td>No</td>
        </tr>
        <tr>
            <td>Link ticket folder</td>
            <td>Yes</td>
            <td>Yes</td>
            <td>No</td>
        </tr>
        </tbody>
        </table>
    </div>

    <h3 class="headingThree">Assets Folder</h3>
    <div class="permission-table">
        <table>
        <thead>
            <th>Permission</th>
            <th>Admin</th>
            <th>Editor</th>
            <th>Viewer</th>
        </thead>
        <tbody>
        <tr>
            <td>Upload files</td>
            <td>Yes</td>
            <td>Yes</td>
            <td>No</td>
        </tr>
        <tr>
            <td>Create folders</td>
            <td>Yes</td>
            <td>Yes</td>
            <td>No</td>
        </tr>
        <tr>
            <td>Edit details</td>
            <td>Yes</td>
            <td>Yes</td>
            <td>No</td>
        </tr>
        <tr>
            <td>Delete assets</td>
            <td>Yes</td>
            <td>Yes(only created by themselves)</td>
            <td>No</td>
        </tr>
        <tr>
            <td>Share</td>
            <td>Yes</td>
            <td>Yes</td>
            <td>Yes</td>
        </tr>
        <tr>
            <td>Download assets</td>
            <td>Yes</td>
            <td>Yes</td>
            <td>No</td>
        </tr>
        </tbody>
        </table>
    </div>

    <h3 class="headingThree">Flowcess Folder</h3>
    <div class="permission-table">
        <table>
        <thead>
            <th>Permission</th>
            <th>Admin</th>
            <th>Editor</th>
            <th>Viewer</th>
        </thead>
        <tbody>
        <tr>
            <td>Create AI Agents</td>
            <td>Yes</td>
            <td>Yes</td>
            <td>No</td>
        </tr>
        <tr>
            <td>Publish AI Agents</td>
            <td>Yes</td>
            <td>Yes</td>
            <td>No</td>
        </tr>
        <tr>
            <td>Run / trigger AI Agents</td>
            <td>Yes</td>
            <td>Yes</td>
            <td>No</td>
        </tr>
        <tr>
            <td>View instance/ log / output / user information</td>
            <td>Yes</td>
            <td>Yes</td>
            <td>No</td>
        </tr>
        <tr>
            <td>Edit AI Agents details</td>
            <td>Yes</td>
            <td>Yes</td>
            <td>No</td>
        </tr>
        <tr>
            <td>Edit / update AI Agents from AI Studio</td>
            <td>Yes</td>
            <td>Yes</td>
            <td>No</td>
        </tr>
        </tbody>
        </table>
    </div>
      `,
  },
  "roles-crm": {
    title: "CRM Roles and Permissions",
    content:`
    <p>CRM roles and permissions follow the same structure as anydone’s project roles. As the Workspace Owner and Admin are assigned as Project Owners in every project, therefore, they have full authority to manage and control all aspects of the CRM.</p>
    <p>CRM roles are static, and their permissions cannot be customized further. Likewise, Project Owners can add members to the CRM by assigning specific roles to members, just as they do in project folders. They can add members to CRM with roles such as Admin, Editor, and Viewer.</p>
    <p>Permissions for CRM roles like Admin, Editor, and Viewer are the same as project folders. Owners can assign roles further in the CRM as needed. But you need to remember that, regardless of the role name, permissions stay the same.</p>

    <div class="permission-table">
        <table>
        <thead>
            <th>Permission</th>
            <th>Admin</th>
            <th>Editor</th>
            <th>Viewer</th>
        </thead>
        <tbody>
        <tr>
            <td>Create CRM</td>
            <td>Yes</td>
            <td>Yes</td>
            <td>No</td>
        </tr>
        <tr>
            <td>Be a responder</td>
            <td>Yes</td>
            <td>Yes</td>
            <td>No</td>
        </tr>
        <tr>
            <td>Message Filter</td>
            <td>Yes</td>
            <td>Yes</td>
            <td>Yes</td>
        </tr>
        <tr>
            <td>Enable / Disable AI agent reply</td>
            <td>Yes</td>
            <td>Yes</td>
            <td>No</td>
        </tr>
        <tr>
            <td>Mark conversation as spam/ not spam</td>
            <td>Yes</td>
            <td>Yes</td>
            <td>No</td>
        </tr>
        <tr>
            <td>Add / remove labels</td>
            <td>Yes</td>
            <td>Yes</td>
            <td>No</td>
        </tr>
        <tr>
            <td>Change conversation status</td>
            <td>Yes</td>
            <td>Yes</td>
            <td>No</td>
        </tr>
        <tr>
            <td>Update customer information</td>
            <td>Yes</td>
            <td>Yes</td>
            <td>No</td>
        </tr>
        <tr>
            <td>Upload / View files  notes</td>
            <td>Yes</td>
            <td>Yes</td>
            <td>No</td>
        </tr>
        <tr>
            <td>Link Ticket Folder</td>
            <td>Yes (Requires ticket, tasks folder access)</td>
            <td>No</td>
            <td>No</td>
        </tr>
        <tr>
            <td>Create ticket</td>
            <td>Yes (Requires ticket, tasks folder access)</td>
            <td>Yes (Requires ticket, tasks folder access)</td>
            <td>No</td>
        </tr>
        <tr>
            <td>Convert leads to contacts</td>
            <td>Yes</td>
            <td>Yes</td>
            <td>No</td>
        </tr>
        <tr>
            <td>Convert contacts to customer</td>
            <td>Yes</td>
            <td>Yes</td>
            <td>No</td>
        </tr>
        <tr>
            <td>Create / Delete / Cancel / Reschedule campaign</td>
            <td>Yes</td>
            <td>Yes (only created by them)</td>
            <td>No</td>
        </tr>
        <tr>
            <td>Add / Remove source</td>
            <td>Yes</td>
            <td>No</td>
            <td>No</td>
        </tr>
        <tr>
            <td>Enable / Disable source status</td>
            <td>Yes</td>
            <td>No</td>
            <td>No</td>
        </tr>
        <tr>
            <td>Create / Add / Remove agent</td>
            <td>Yes</td>
            <td>No</td>
            <td>No</td>
        </tr>
        <tr>
            <td>Change Agent role</td>
            <td>Yes</td>
            <td>No</td>
            <td>No</td>
        </tr>
        <tr>
            <td>Enable / Disable Incoming or Outgoing hooks</td>
            <td>Yes</td>
            <td>No</td>
            <td>No</td>
        </tr>
        <tr>
            <td>Add / Edit / Delete / Change role</td>
            <td>Yes</td>
            <td>No</td>
            <td>No</td>
        </tr>
        <tr>
            <td>Custom Field</td>
            <td>Yes</td>
            <td>No</td>
            <td>No</td>
        </tr>
        <tr>
            <td>Add customer manually</td>
            <td>Yes</td>
            <td>Yes</td>
            <td>No</td>
        </tr>
        <tr>
            <td>Edit customer information in leads, contacts, customers</td>
            <td>Yes</td>
            <td>Yes</td>
            <td>No</td>
        </tr>
        </tbody>
        </table>
    </div>
    `
  },
};
